import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Landmark } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";

const NAV = [
  ["home", "/"], ["services", "/services"], ["verification", "/verification"],
  ["calculators", "/calculators"], ["registration", "/registration"], ["rent", "/rent-agreement"],
  ["estamping", "/e-stamping"], ["garvi", "/garvi"], ["blog", "/blog"], ["contact", "/contact"],
];

export function Header() {
  const { t, lang, changeLang, user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isStaff = user && ["operator", "manager", "admin", "super_admin"].includes(user.role);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <span className="w-9 h-9 rounded-lg bg-primary text-white flex items-center justify-center">
            <Landmark size={20} />
          </span>
          <span className="font-display font-800 text-xl leading-none">
            <span className="text-primary font-bold">Doc</span><span className="text-secondary font-bold">Galaxy</span>
            <span className="block text-[10px] text-muted-foreground font-normal tracking-wide">Gujarat Property Services</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map(([key, to]) => (
            <NavLink key={key} to={to} data-testid={`nav-${key}`}
              className={({ isActive }) => `px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors ${isActive ? "text-primary bg-primary/5" : "text-foreground/70 hover:text-primary"}`}>
              {t(key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => changeLang(lang === "en" ? "gu" : "en")} data-testid="language-switcher"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:border-primary hover:text-primary transition-colors">
            <Globe size={14} /> {lang === "en" ? "ગુજરાતી" : "English"}
          </button>
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="header-dashboard-btn"
                onClick={() => navigate(isStaff ? "/admin" : "/dashboard")}>
                {isStaff ? "Admin" : t("dashboard")}
              </Button>
              <Button size="sm" variant="ghost" onClick={logout} data-testid="header-logout-btn">{t("logout")}</Button>
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn">{t("login")}</Button>
              <Button size="sm" onClick={() => navigate("/register")} data-testid="header-get-started-btn"
                className="bg-secondary hover:bg-secondary/90">{t("get_started")}</Button>
            </div>
          )}
          <button className="xl:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="xl:hidden border-t border-border bg-white px-4 py-3 grid grid-cols-2 gap-1" data-testid="mobile-nav-menu">
          {NAV.map(([key, to]) => (
            <NavLink key={key} to={to} onClick={() => setOpen(false)} data-testid={`mnav-${key}`}
              className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground/80 hover:bg-muted">
              {t(key)}
            </NavLink>
          ))}
          {!user && (
            <>
              <NavLink to="/login" onClick={() => setOpen(false)} data-testid="mnav-login"
                className="px-3 py-2.5 rounded-md text-sm font-semibold text-primary hover:bg-muted">{t("login")}</NavLink>
              <NavLink to="/register" onClick={() => setOpen(false)} data-testid="mnav-register"
                className="px-3 py-2.5 rounded-md text-sm font-semibold text-secondary hover:bg-muted">{t("get_started")}</NavLink>
            </>
          )}
          {user && (
            <NavLink to={isStaff ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} data-testid="mnav-dashboard"
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-primary hover:bg-muted">
              {isStaff ? "Admin Panel" : t("dashboard")}
            </NavLink>
          )}
        </nav>
      )}
    </header>
  );
}
